import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./Tooltip.module.css";

export default function Tooltip({ content, offset = 14, delay = 120, children }) {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ x: 0, y: 0 });
    const [mounted, setMounted] = useState(false);
    const id = useId();
    const rafRef = useRef(null);
    const timerRef = useRef(null);
    const last = useRef({ x: 0, y: 0 });

    useEffect(() => setMounted(true), []);

    const clearTimer = () => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const onEnter = () => {
        clearTimer();
        timerRef.current = window.setTimeout(() => setOpen(true), delay);
    };

    const onLeave = () => {
        clearTimer();
        setOpen(false);
    };

    const onMove = (e) => {
        last.current = { x: e.clientX, y: e.clientY };
        if (rafRef.current == null) {
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;

                const tipW = 260;
                const tipH = 32;
                const pad = 8;

                let x = last.current.x + offset;
                let y = last.current.y + offset;

                const vw = window.innerWidth;
                const vh = window.innerHeight;

                if (x + tipW + pad > vw) x = Math.max(pad, last.current.x - offset - tipW);
                if (y + tipH + pad > vh) y = Math.max(pad, last.current.y - offset - tipH);

                setCoords({ x, y });
            });
        }
    };

    const child = React.cloneElement(children, {
        onMouseEnter: (e) => {
            children.props.onMouseEnter?.(e);
            onEnter();
        },
        onMouseLeave: (e) => {
            children.props.onMouseLeave?.(e);
            onLeave();
        },
        onFocus: (e) => {
            children.props.onFocus?.(e);
            onEnter();
        },
        onBlur: (e) => {
            children.props.onBlur?.(e);
            onLeave();
        },
        onMouseMove: (e) => {
            children.props.onMouseMove?.(e);
            onMove(e);
        },
        "aria-describedby": open ? id : undefined,
    });

    return (
        <>
            {child}
            {mounted &&
                createPortal(
                    <div
                        id={id}
                        className={`${styles.tooltip} ${open ? styles.show : ""}`}
                        style={{ left: coords.x, top: coords.y }}
                        role="tooltip"
                    >
                        {content}
                    </div>,
                    document.body
                )}
        </>
    );
}
